import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import DiyLayout from "../../components/Layout/DiyLayout";
import { useAuth } from "../../hooks/useAuth";
import { billingApi } from "../../api/billingApi";
import type { BillingSubscription } from "../../api/billingApi";
import { ScrollReveal } from "../../components/diy/ScrollReveal";
import "../../styles/diy-animations.css";

const glass = {
  background: "rgba(255, 255, 255, 0.03)",
  border: "1px solid rgba(255, 255, 255, 0.07)",
};

const DiySettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [sub, setSub] = useState<BillingSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [securityOpen, setSecurityOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");

  const loadSub = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const s = await billingApi.getSubscription();
      setSub(s);
    } catch {
      setMsg("Could not load subscription.");
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSub();
  }, [loadSub]);

  const cancel = async () => {
    if (!window.confirm("Cancel your DIY subscription? You will lose access at the end of the billing period.")) {
      return;
    }
    setCancelBusy(true);
    setMsg("");
    try {
      await billingApi.cancelSubscription();
      await loadSub();
      setMsg("Subscription cancelled.");
    } catch {
      setMsg("Could not cancel. Try again or contact support.");
    } finally {
      setCancelBusy(false);
    }
  };

  const displayName = user?.user ? `${user.user.firstName} ${user.user.lastName}`.trim() : "—";
  const email = user?.user?.email ?? user?.operatorEmail ?? "—";

  const fieldBox = "rounded-xl py-3 px-4";
  const fieldBoxStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.07)",
  };

  return (
    <DiyLayout>
      <div className="mx-auto max-w-lg md:max-w-xl">
        <header className="mb-6">
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 700, fontSize: 22, color: "#F1F5F9" }}>Settings</h1>
        </header>

        <div className="flex flex-col gap-4">
          <ScrollReveal>
            <section className="rounded-2xl p-5" style={glass}>
              <h2
                className="mb-4"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  color: "#10B981",
                  letterSpacing: 2,
                }}
              >
                ACCOUNT
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="mb-1 text-xs font-medium" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
                    Name
                  </p>
                  <div className={fieldBox} style={fieldBoxStyle}>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, color: "#F1F5F9" }}>{displayName}</p>
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
                    Email
                  </p>
                  <div className={fieldBox} style={fieldBoxStyle}>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: 14, color: "#F1F5F9" }}>{email}</p>
                  </div>
                </div>
              </div>
            </section>
          </ScrollReveal>

          <ScrollReveal delay={0.05}>
            <section className="rounded-2xl p-5" style={glass}>
              <h2
                className="mb-4"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  color: "#10B981",
                  letterSpacing: 2,
                }}
              >
                SECURITY
              </h2>
              <button
                type="button"
                onClick={() => setSecurityOpen((o) => !o)}
                className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent py-3 text-left"
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  color: "#E2E8F0",
                }}
              >
                Change Password
                <ChevronRight
                  className="h-4 w-4 transition-transform"
                  style={{ color: "#64748B", transform: securityOpen ? "rotate(90deg)" : "none" }}
                />
              </button>
              {securityOpen ? (
                <div className="mt-4 space-y-3">
                  <label className="block text-xs" style={{ color: "#94A3B8" }}>
                    Current password
                    <input
                      type="password"
                      value={pwCurrent}
                      onChange={(e) => setPwCurrent(e.target.value)}
                      className="mt-1 w-full rounded-xl border px-3 py-2 text-sm text-white"
                      style={{
                        borderColor: "rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.04)",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    />
                  </label>
                  <label className="block text-xs" style={{ color: "#94A3B8" }}>
                    New password
                    <input
                      type="password"
                      value={pwNew}
                      onChange={(e) => setPwNew(e.target.value)}
                      className="mt-1 w-full rounded-xl border px-3 py-2 text-sm text-white"
                      style={{
                        borderColor: "rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.04)",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    />
                  </label>
                  <label className="block text-xs" style={{ color: "#94A3B8" }}>
                    Confirm password
                    <input
                      type="password"
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      className="mt-1 w-full rounded-xl border px-3 py-2 text-sm text-white"
                      style={{
                        borderColor: "rgba(255,255,255,0.08)",
                        background: "rgba(255,255,255,0.04)",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="mt-2 w-full cursor-pointer rounded-xl border-0 py-3 font-semibold text-white transition-transform hover:scale-[1.01]"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                      boxShadow: "0 4px 15px rgba(16,185,129,0.25)",
                    }}
                    onClick={() => {
                      window.location.href = "/forgot-password";
                    }}
                  >
                    Update Password
                  </button>
                  <p className="text-xs" style={{ color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>
                    You&apos;ll be redirected to the secure password reset flow.
                  </p>
                </div>
              ) : null}
            </section>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <section
              className="rounded-2xl p-5"
              style={{
                background: "rgba(16, 185, 129, 0.06)",
                border: "1px solid rgba(16, 185, 129, 0.15)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              <h2
                className="mb-4"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  color: "#10B981",
                  letterSpacing: 2,
                }}
              >
                SUBSCRIPTION
              </h2>
              {loading ? (
                <p className="text-sm" style={{ color: "#94A3B8", fontFamily: "'DM Sans', sans-serif" }}>
                  Loading…
                </p>
              ) : sub ? (
                <div className="space-y-2">
                  <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                    {sub.tier || "DIY Plan"}
                  </p>
                  <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#94A3B8" }}>$49.99/month</p>
                  <span
                    className="inline-block rounded-full px-3 py-1 text-[10px] font-semibold"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      background: "rgba(16,185,129,0.12)",
                      color: "#10B981",
                    }}
                  >
                    {sub.status}
                  </span>
                  {sub.currentPeriodEnd ? (
                    <p className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif", color: "#94A3B8" }}>
                      Next billing: {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  ) : null}
                  {(sub.status === "active" || sub.status === "trialing") && (
                    <button
                      type="button"
                      onClick={() => void cancel()}
                      disabled={cancelBusy}
                      className="mt-4 w-full cursor-pointer rounded-xl border px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-40"
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        background: "rgba(239, 68, 68, 0.1)",
                        borderColor: "rgba(239, 68, 68, 0.2)",
                        color: "#EF4444",
                      }}
                    >
                      {cancelBusy ? "Cancelling…" : "Cancel Subscription"}
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-sm" style={{ color: "#64748B", fontFamily: "'DM Sans', sans-serif" }}>
                  No subscription data.
                </p>
              )}
              {msg ? (
                <p className="mt-3 text-sm" style={{ color: "#FDE68A", fontFamily: "'DM Sans', sans-serif" }}>
                  {msg}
                </p>
              ) : null}
            </section>
          </ScrollReveal>

          <ScrollReveal delay={0.12}>
            <section className="rounded-2xl p-5" style={{ ...glass, border: "1px solid rgba(239,68,68,0.15)" }}>
              <h2
                className="mb-4"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: 10,
                  color: "#EF4444",
                  letterSpacing: 2,
                }}
              >
                DANGER ZONE
              </h2>
              <button
                type="button"
                className="w-full cursor-pointer rounded-xl border px-4 py-3 text-sm font-semibold"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  background: "rgba(239, 68, 68, 0.1)",
                  borderColor: "rgba(239, 68, 68, 0.2)",
                  color: "#EF4444",
                }}
                onClick={() => {
                  window.location.href = "mailto:support@intelliwarrior.com?subject=Delete%20my%20DIY%20account";
                }}
              >
                Delete Account
              </button>
              <p className="mt-2 text-xs" style={{ color: "#EF4444", fontFamily: "'DM Sans', sans-serif" }}>
                This action cannot be undone — contact support to delete your account safely.
              </p>
            </section>
          </ScrollReveal>

          <section className="rounded-2xl p-5" style={glass}>
            <h2 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 15, color: "#F1F5F9" }}>Legal</h2>
            <div className="mt-3 flex flex-wrap gap-4 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              <Link to="/terms" style={{ color: "#0EA5E9" }} className="no-underline hover:underline">
                Terms of Service
              </Link>
              <Link to="/privacy" style={{ color: "#0EA5E9" }} className="no-underline hover:underline">
                Privacy Policy
              </Link>
            </div>
          </section>

          <button
            type="button"
            onClick={() => logout()}
            className="w-full cursor-pointer rounded-xl border py-3 text-sm transition-colors"
            style={{
              fontFamily: "'DM Sans', sans-serif",
              borderColor: "rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.04)",
              color: "#E2E8F0",
            }}
          >
            Log out
          </button>
        </div>
      </div>
    </DiyLayout>
  );
};

export default DiySettingsPage;
